import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { defaultRouteFor, isAuditRole, isClientRole } from "@/lib/roles";
import Login from "@/pages/Login";
import AuditDashboard from "@/pages/audit/AuditDashboard";
import PartnerDashboard from "@/pages/audit/PartnerDashboard";
import Companies from "@/pages/audit/Companies";
import CompanyDetail from "@/pages/audit/CompanyDetail";
import ClientUsers from "@/pages/audit/ClientUsers";
import DataTools from "@/pages/audit/DataTools";
import ClientDashboard from "@/pages/client/ClientDashboard";
import FounderDashboard from "@/pages/client/FounderDashboard";
import ClientObservations from "@/pages/client/ClientObservations";
import ClientProfilePage from "@/pages/client/ClientProfilePage";
import ObservationDetail from "@/pages/ObservationDetail";

function roleMatches(allowed, role) {
  if (!allowed) return true;
  if (allowed === "audit") return isAuditRole(role);
  if (allowed === "audit_team") return role === "audit_team";
  if (allowed === "client") return isClientRole(role);
  if (Array.isArray(allowed)) return allowed.includes(role);
  return allowed === role;
}

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!roleMatches(role, user.role)) {
    return <Navigate to={defaultRouteFor(user)} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-slate-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={defaultRouteFor(user)} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />

            <Route path="/audit/dashboard" element={<Protected role="audit_team"><AuditDashboard /></Protected>} />
            <Route path="/audit/partner" element={<Protected role="audit"><PartnerDashboard /></Protected>} />
            <Route path="/audit/companies" element={<Protected role="audit"><Companies /></Protected>} />
            <Route path="/audit/companies/:companyId" element={<Protected role="audit"><CompanyDetail /></Protected>} />
            <Route path="/audit/users" element={<Protected role="audit_team"><ClientUsers /></Protected>} />
            <Route path="/audit/data-tools" element={<Protected role="audit_team"><DataTools /></Protected>} />
            <Route path="/audit/observations/:obsId" element={<Protected role="audit"><ObservationDetail /></Protected>} />

            <Route path="/client/dashboard" element={<Protected role="client"><ClientDashboard /></Protected>} />
            <Route path="/client/founder" element={<Protected role="client"><FounderDashboard /></Protected>} />
            <Route path="/client/observations" element={<Protected role="client"><ClientObservations /></Protected>} />
            <Route path="/client/observations/:obsId" element={<Protected role="client"><ObservationDetail /></Protected>} />
            <Route path="/client/profile" element={<Protected role="client"><ClientProfilePage /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="bottom-right" duration={2500} />
      </AuthProvider>
    </div>
  );
}

export default App;
